import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import eyeLogo from '../assets/relationprotector-eye.svg';

export default function RoleSelectPage() {
  const navigate = useNavigate();
  const [selectedRole, setSelectedRole] = useState(null);

  const handleRoleSelect = (role) => {
    setSelectedRole(role);
    localStorage.setItem('selectedRole', role);
    toast.success(`Welcome! Proceeding as ${role === 'parent' ? 'Parent' : 'Child'}...`);
    setTimeout(() => {
      navigate('/login');
    }, 600);
  };

  return (
    <div className="role-select-container">
      <div className="role-select-wrapper">
        {/* Logo Section */}
        <div className="role-select-logo">
          <div className="role-select-badge"><img src={eyeLogo} alt="RelationProtector" className="brand-logo-img" /></div>
          <h1>RelationProtector</h1>
          <p>Parental Control & Child Safety System</p>
        </div>

        {/* Title */}
        <div className="role-select-header">
          <h2>Choose Your Role</h2>
          <p className="role-select-subtitle">Select whether you're a parent or child</p>
        </div>

        {/* Role Cards */}
        <div className="role-cards-container">
          {/* Parent Card */}
          <div
            className={`role-card role-card-parent ${selectedRole === 'parent' ? 'selected' : ''}`}
            onClick={() => handleRoleSelect('parent')}
          >
            <div className="role-card-icon">👨‍👩‍👧</div>
            <h3>Parent Account</h3>
            <p className="role-card-description">
              Monitor and manage your children's devices with full control panel
            </p>
            <ul className="role-features">
              <li>📍 Real-time location tracking</li>
              <li>📊 Usage analytics & reports</li>
              <li>🎮 Device control features</li>
              <li>💬 Chat with your children</li>
              <li>🆘 SOS alert notifications</li>
              <li>📋 Access logs & monitoring</li>
            </ul>
            <div className={`role-card-button ${selectedRole === 'parent' ? 'active' : ''}`}>
              {selectedRole === 'parent' ? '✓ Selected' : 'Select'}
            </div>
          </div>

          {/* Child Card */}
          <div
            className={`role-card role-card-child ${selectedRole === 'child' ? 'selected' : ''}`}
            onClick={() => handleRoleSelect('child')}
          >
            <div className="role-card-icon">👦</div>
            <h3>Child Account</h3>
            <p className="role-card-description">
              Safe device access with parental oversight and AI guidance
            </p>
            <ul className="role-features">
              <li>🏠 Clean, child-friendly interface</li>
              <li>💬 Chat with your guardian</li>
              <li>🤖 AI help guide assistant</li>
              <li>🔐 Secure app permissions</li>
              <li>📍 Location tracking & safety</li>
              <li>🎯 Smart monitoring features</li>
            </ul>
            <div className={`role-card-button ${selectedRole === 'child' ? 'active' : ''}`}>
              {selectedRole === 'child' ? '✓ Selected' : 'Select'}
            </div>
          </div>
        </div>

        {/* Info Footer */}
        <div className="role-select-footer">
          <p>🔒 All monitoring requires explicit consent from the child device</p>
          <p style={{ fontSize: '12px', marginTop: '8px', color: 'var(--text-muted)' }}>
            Secure end-to-end encrypted connection
          </p>
        </div>
      </div>

      <style>{`
        .role-select-container {
          min-height: 100vh;
          background:
            radial-gradient(circle at 12% 20%, rgba(59,130,246,0.16), transparent 35%),
            radial-gradient(circle at 82% 8%, rgba(16,185,129,0.14), transparent 32%),
            radial-gradient(circle at 80% 82%, rgba(245,158,11,0.12), transparent 30%),
            var(--bg-primary);
          display: flex;
          justify-content: center;
          align-items: center;
          padding: 20px;
          font-family: 'Manrope', -apple-system, sans-serif;
        }

        .role-select-wrapper {
          width: 100%;
          max-width: 1000px;
          animation: fadeIn 0.5s ease-out;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .role-select-logo {
          text-align: center;
          margin-bottom: 50px;
        }

        .role-select-badge {
          width: 74px;
          height: 74px;
          margin-bottom: 12px;
          display: inline-block;
          animation: bounce 2s infinite;
        }

        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }

        .role-select-logo h1 {
          font-size: 48px;
          font-weight: 800;
          color: var(--text-primary);
          margin-bottom: 8px;
          background: var(--gradient-blue);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .role-select-logo p {
          color: var(--text-secondary);
          font-size: 14px;
          letter-spacing: 0.5px;
        }

        .role-select-header {
          text-align: center;
          margin-bottom: 40px;
        }

        .role-select-header h2 {
          font-size: 32px;
          font-weight: 700;
          color: var(--text-primary);
          margin-bottom: 8px;
        }

        .role-select-subtitle {
          color: var(--text-secondary);
          font-size: 16px;
        }

        .role-cards-container {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
          gap: 24px;
          margin-bottom: 40px;
        }

        .role-card {
          background: linear-gradient(135deg, rgba(17,24,39,0.8), rgba(13,21,40,0.8));
          backdrop-filter: blur(10px);
          border: 1px solid var(--border);
          border-radius: 24px;
          padding: 32px;
          cursor: pointer;
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
        }

        .role-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: var(--gradient-blue);
          opacity: 0;
          transition: opacity 0.3s ease;
          pointer-events: none;
        }

        .role-card-parent::before {
          background: var(--gradient-blue);
        }

        .role-card-child::before {
          background: var(--gradient-green);
        }

        .role-card:hover {
          border-color: var(--border-active);
          transform: translateY(-8px);
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
        }

        .role-card.selected {
          border-color: var(--blue);
          box-shadow: 0 0 30px rgba(59, 130, 246, 0.5);
        }

        .role-card.selected::before {
          opacity: 0.08;
        }

        .role-card > * {
          position: relative;
          z-index: 1;
        }

        .role-card-icon {
          font-size: 48px;
          margin-bottom: 16px;
          display: inline-block;
          animation: float 3s ease-in-out infinite;
        }

        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }

        .role-card h3 {
          font-size: 20px;
          font-weight: 700;
          color: var(--text-primary);
          margin-bottom: 8px;
        }

        .role-card-description {
          color: var(--text-secondary);
          font-size: 14px;
          margin-bottom: 20px;
          line-height: 1.5;
        }

        .role-features {
          list-style: none;
          margin-bottom: 20px;
        }

        .role-features li {
          color: var(--text-secondary);
          font-size: 13px;
          margin-bottom: 8px;
          padding-left: 0;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .role-features li::before {
          content: '✓';
          color: var(--green);
          font-weight: 700;
        }

        .role-card-button {
          background: var(--gradient-blue);
          color: white;
          border: none;
          border-radius: 12px;
          padding: 12px 24px;
          font-weight: 600;
          text-align: center;
          cursor: pointer;
          transition: all 0.3s ease;
          font-size: 14px;
        }

        .role-card-button:hover {
          transform: scale(1.05);
          box-shadow: 0 8px 16px rgba(59, 130, 246, 0.3);
        }

        .role-card-button.active {
          background: var(--green);
        }

        .role-select-footer {
          text-align: center;
          padding: 24px;
          background: rgba(59, 130, 246, 0.1);
          border: 1px solid var(--border);
          border-radius: 16px;
          color: var(--text-secondary);
          font-size: 13px;
        }

        @media (max-width: 768px) {
          .role-cards-container {
            grid-template-columns: 1fr;
          }

          .role-select-logo h1 {
            font-size: 36px;
          }

          .role-select-header h2 {
            font-size: 24px;
          }

          .role-card {
            padding: 24px;
          }

          .role-card-icon {
            font-size: 36px;
          }
        }
      `}</style>
    </div>
  );
}
